/**
 * @fileoverview Band-card illustration: a single band card as rendered in the canvas (Phase 5, J3).
 * @module ui-admin/help/illustrations/IllustrationBandCard
 *
 * Responsibilities: - Faithfully recreate one band card (drag handle, label, group tag, enabled
 *                     toggle, overflow ⋮ menu, and the ? help button) with numbered callouts.
 * Dependencies:     - React; shared illustration primitives.
 * Architecture:     - Presentation-only SVG. No state, no data, no side effects.
 * Notes:            - Mirrors the BandCard header row in DocumentCanvasBuilder; update together.
 */

import React from 'react';
import { CalloutBadge, IllustrationFigure, ILLUS } from './illustrationPrimitives';

/**
 * @component IllustrationBandCard
 * Purpose: Annotated diagram of a single band card and each of its controls.
 */
export function IllustrationBandCard() {
  return (
    <IllustrationFigure
      title="A band card and its controls"
      viewBox="0 0 400 120"
      legend={[
        'Drag handle: grab here to reorder the band.',
        'Band label and group tag (e.g. Letterhead \u00b7 HEADER).',
        'Enabled toggle: include or skip this band when rendering.',
        'Overflow menu (\u22ee): move, duplicate, or delete the band.',
        'Help (?) button: open contextual help for this band kind.',
      ]}
    >
      <rect x={2} y={2} width={396} height={116} rx={8} fill={ILLUS.gray100} stroke={ILLUS.gray200} />
      {/* the card itself (selected state: orange ring) */}
      <rect x={24} y={30} width={352} height={58} rx={8} fill={ILLUS.white} stroke={ILLUS.orange} strokeWidth={2} />
      {/* drag handle */}
      <g>
        <circle cx={44} cy={52} r={1.6} fill={ILLUS.gray400} />
        <circle cx={44} cy={59} r={1.6} fill={ILLUS.gray400} />
        <circle cx={44} cy={66} r={1.6} fill={ILLUS.gray400} />
        <circle cx={50} cy={52} r={1.6} fill={ILLUS.gray400} />
        <circle cx={50} cy={59} r={1.6} fill={ILLUS.gray400} />
        <circle cx={50} cy={66} r={1.6} fill={ILLUS.gray400} />
      </g>
      {/* label + group tag */}
      <text x={66} y={62} fontSize={12} fontWeight={700} fill={ILLUS.gray800}>Letterhead</text>
      <text x={140} y={62} fontSize={9} letterSpacing="0.05em" fill={ILLUS.gray400}>HEADER</text>
      {/* enabled toggle */}
      <rect x={286} y={52} width={11} height={11} rx={2} fill={ILLUS.white} stroke={ILLUS.gray400} />
      <path d="M288.5 57.5 l2 2 l4 -4" fill="none" stroke={ILLUS.orange} strokeWidth={1.6} />
      {/* overflow menu */}
      <circle cx={324} cy={54} r={1.7} fill={ILLUS.gray500} />
      <circle cx={324} cy={59} r={1.7} fill={ILLUS.gray500} />
      <circle cx={324} cy={64} r={1.7} fill={ILLUS.gray500} />
      {/* help button */}
      <circle cx={352} cy={59} r={8} fill={ILLUS.white} stroke={ILLUS.gray400} />
      <text x={352} y={62.5} textAnchor="middle" fontSize={10} fontWeight={700} fill={ILLUS.gray500}>?</text>

      {/* Callouts */}
      <CalloutBadge n={1} x={47} y={24} />
      <CalloutBadge n={2} x={90} y={24} />
      <CalloutBadge n={3} x={291} y={40} />
      <CalloutBadge n={4} x={324} y={40} />
      <CalloutBadge n={5} x={352} y={40} />
    </IllustrationFigure>
  );
}
